import { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Package, Truck, Clock, Share2, ChevronRight, Hash, Layers, Info, Mail, CheckCircle2, ShieldCheck, Wrench } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ScrollToTop from "@/components/ScrollToTop";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Helmet } from "react-helmet-async";
import { fetchProductById, fetchProducts, Product } from "@/data/productsData";
import ProductNotFound from "./ProductNotFound";
import { shuffleArray } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { useQuery } from "@tanstack/react-query";

const FALLBACK_IMAGE = "/catalog.png";

/**
 * Google's own image CDN (lh3.googleusercontent.com) can serve a resized
 * version of an image on the fly via a size suffix — no processing needed
 * on our side. Only applies to Google-hosted URLs; anything else (our own
 * /assets, /catalog.png) passes through unchanged.
 * Width-only, no crop: matches object-contain rendering (shows the full
 * image, scaled down, never cut off) — appending "-c" would instead crop
 * to a square and clip parts of non-square or off-center photos.
 */
const getSizedGoogleImage = (url: string | null | undefined, width: number): string => {
  if (!url || !url.includes('googleusercontent.com')) return url || '';
  const base = url.replace(/=[\w-]+$/, '');
  return `${base}=w${width}`;
};

const ProductDetail = () => {
  const { productId } = useParams<{ productId: string }>();
  const { language, t, getLocalizedPath } = useLanguage();

  // Query for the specific product
  const { data: product, isLoading, isError } = useQuery<Product>({
    queryKey: ['product', productId],
    queryFn: () => fetchProductById(productId!),
    enabled: !!productId,
    refetchOnWindowFocus: false, // don't refetch just because the tab regained focus
    staleTime: 3 * 60 * 1000, // matches the 5-min CDN cache on this endpoint closely enough
  });

  // Query only products of the same type, filtered server-side — avoids
  // downloading the entire catalog just to find 4 related items.
  // page=1 shuffles the FULL type-filtered set server-side before slicing to
  // `limit`, so this is a random sample across every matching product — not
  // just the first N in stable order (which page=2+ would give). Never
  // cached (no-store) on the server, so every *fresh* fetch draws a new
  // sample — but refetchOnWindowFocus is off and staleTime keeps this
  // client-side result around, so switching tabs and back doesn't yank the
  // related products out from under the user with a different random set.
  const { data: relatedProductsData } = useQuery({
    queryKey: ['products', 'related', product?.type],
    queryFn: () => fetchProducts({ type: product!.type, limit: 40, page: 1 }),
    enabled: !!product?.type,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // keep the same random sample for the life of this visit
  });

  // Generate related products
  const relatedProducts = useMemo(() => {
    if (!product || !relatedProductsData?.products) return [];
    const related = relatedProductsData.products.filter(p => p.id !== product.id);
    return shuffleArray(related).slice(0, 4);
  }, [product, relatedProductsData]);

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: product?.['name_' + language],
          text: `${product?.['name_' + language]} - KM KIT`,
          url: window.location.href,
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        toast.success(t('product.linkCopied'));
      }
    } catch (error) {
      console.error("Error sharing:", error);
      toast.error(t('product.shareError'));
    }
  };

  const productName = product?.['name_' + language] || product?.name_source || '';
  const productDescription = product?.['description_' + language] || product?.description_source || '';

  // Applications block: prefer the visitor's language; fall back to whatever
  // language the AI generation has produced so far (usually source_lang),
  // so the block isn't hidden just because translation hasn't caught up yet.
  const applicationsText =
    product?.['applications_' + language] ||
    (product?.source_lang ? product?.['applications_' + product.source_lang] : '') ||
    '';
  const applicationsIsTranslated = !!product?.['applications_' + language];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="animate-pulse">
            <div className="h-6 bg-muted rounded w-1/4 mb-8"></div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="aspect-square bg-muted rounded-xl max-h-[300px]"></div>
              <div className="lg:col-span-2 space-y-4">
                <div className="h-8 bg-muted rounded w-3/4"></div>
                <div className="h-4 bg-muted rounded w-1/2"></div>
                <div className="h-20 bg-muted rounded w-full"></div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (isError || !product) {
    return <ProductNotFound />;
  }

  // Build a content-rich, unique meta description from the actual product text.
  // Leads with the product name so it's visible even if the snippet gets cut,
  // and even when Google chooses to ignore the <title> tag for the SERP snippet.
  // Falls back to the generic template only when the product has no description at all.
  const buildMetaDescription = (): string => {
    const MAX_LENGTH = 160;
    const suffix = ` | ${product.manufacturer || 'KM KIT'}. ID: ${product.id}`;

    if (productDescription) {
      const lead = productName ? `${productName} ` : '';
      const combined = `${lead}${productDescription}`.trim();
      const available = MAX_LENGTH - suffix.length;
      const trimmedDescription =
        combined.length > available
          ? `${combined.slice(0, Math.max(0, available - 1)).trim()}…`
          : combined;
      return `${trimmedDescription}${suffix}`;
    }

    return `${productName} - ${product.type}. Industrial component from KM KIT. Request pricing and availability. ID: ${product.id}`;
  };

  const metaDescription = buildMetaDescription();
  const ogDescription =
    productDescription
      ? (() => {
          const combined = `${productName ? `${productName} ` : ''}${productDescription}`.trim();
          return combined.length > 200 ? `${combined.slice(0, 199).trim()}…` : combined;
        })()
      : `${product.type}. Industrial component. ID: ${product.id}`;

  // SEO structured data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": productName,
    "description": productDescription,
    "image": product.image || FALLBACK_IMAGE,
    "sku": product.id,
    "brand": {
      "@type": "Brand",
      "name": product.manufacturer || "KM KIT"
    },
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock",
      "priceCurrency": "EUR",
      "seller": {
        "@type": "Organization",
        "name": "KM KIT mbH"
      }
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{`${productName} | ${product.type || 'Industrial Component'} - KM KIT`}</title>
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={`${productName}, ${product.type}, ${product.manufacturer || 'industrial'}, spare parts, components, KM KIT`} />
        <meta property="og:title" content={`${productName} - KM KIT`} />
        <meta property="og:description" content={ogDescription} />
        <meta property="og:image" content={product.image || FALLBACK_IMAGE} />
        <meta property="og:type" content="product" />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
                {['de', 'en', 'es'].map(lang => (
          <link
            key={lang}
            rel="alternate"
            hrefLang={lang}
            href={`https://www.kmkit.com/${lang}/products/${product.id}`}
          />
        ))}
        <link
          rel="alternate"
          hrefLang="x-default"
          href={`https://www.kmkit.com/en/products/${product.id}`}
        />
      </Helmet>

      <Header />
      <ScrollToTop />
      
      <main className="container mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6 flex-wrap" aria-label="Breadcrumb">
          <Link to={getLocalizedPath("/products")} className="hover:text-foreground transition-colors">
            {t('product.catalog')}
          </Link>
          <ChevronRight className="w-4 h-4" aria-hidden="true" />
          {product.type && (
            <>
              <Link
                to={getLocalizedPath(`/products?type=${encodeURIComponent(product.type)}`)}
                className="hover:text-foreground transition-colors"
              >
                {product.type}
              </Link>
              <ChevronRight className="w-4 h-4" aria-hidden="true" />
            </>
          )}
          <span className="text-foreground font-medium truncate max-w-[200px]">
            {productName || t('product.comingSoon')}
          </span>
        </nav>

        <Link 
          to={getLocalizedPath("/products")} 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('product.backToCatalog')}
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 mb-8">
            {/* Product Image */}
            <div className="relative lg:col-span-1">
              <Card className="overflow-hidden border-border">
                <div className="aspect-square relative bg-muted/20 max-h-[320px]">
                  <img
                    src={getSizedGoogleImage(product.image, 640) || FALLBACK_IMAGE}
                    alt={productName || t('product.comingSoon')}
                    className="w-full h-full object-contain p-4"
                    loading="eager"
                    fetchPriority="high"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = FALLBACK_IMAGE;
                    }}
                  />
                </div>
              </Card>
              <Button
                variant="outline"
                size="icon"
                className="absolute top-4 right-4 bg-background/90 backdrop-blur-sm shadow-sm"
                onClick={handleShare}
                aria-label="Share product"
              >
                <Share2 className="w-4 h-4" />
              </Button>
            </div>

            {/* Product Info */}
            <div className="lg:col-span-2 space-y-5">
              {product.type && (
                <Link to={getLocalizedPath(`/products?type=${encodeURIComponent(product.type)}`)}>
                  <Badge
                    variant="secondary"
                    className="text-sm font-medium hover:bg-secondary/70 transition-colors cursor-pointer"
                  >
                    <Layers className="w-3 h-3 mr-1.5" />
                    {product.type}
                  </Badge>
                </Link>
              )}
              
              <h1 className="text-2xl lg:text-3xl font-bold text-foreground leading-tight">
                {productName || t('product.comingSoon')}
              </h1>

              {product.manufacturer && (
                <Link
                  to={getLocalizedPath(`/products?manufacturer=${encodeURIComponent(product.manufacturer)}`)}
                  className="text-sm text-primary font-medium hover:underline inline-block"
                >
                  {product.manufacturer}
                </Link>
              )}

              <div className="flex flex-wrap gap-4 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Hash className="w-4 h-4 text-primary" />
                  <span>{t('product.productId')}:</span>
                  <code className="font-mono font-medium text-foreground bg-muted px-2 py-0.5 rounded">{product.id}</code>
                </div>
              </div>

              <Separator />

              {/* Key Info Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="flex items-center gap-3 p-4 bg-muted/40 rounded-lg border border-border/50">
                  <Package className="w-5 h-5 text-primary flex-shrink-0" />
                  <div>
                    <p className="text-xs text-muted-foreground">{t('product.price')}</p>
                    <p className="text-base font-bold text-foreground">{t('product.onRequest')}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-muted/40 rounded-lg border border-border/50">
                  <Truck className="w-5 h-5 text-primary flex-shrink-0" />
                  <div>
                    <p className="text-xs text-muted-foreground">{t('product.delivery')}</p>
                    <p className="font-medium text-foreground text-sm">{t('product.deliveryDays')}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-muted/40 rounded-lg border border-border/50">
                  <Clock className="w-5 h-5 text-primary flex-shrink-0" />
                  <div>
                    <p className="text-xs text-muted-foreground">{t('product.availability')}</p>
                    <p className="font-medium text-foreground text-sm">{t('product.check')}</p>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <div className="pt-2">
                <Button asChild size="lg" className="bg-accent hover:bg-accent/90 w-full sm:w-auto">
                  <Link to={getLocalizedPath("contacts")}>
                    <Mail className="w-4 h-4 mr-2" />
                    {t('cta.button')}
                  </Link>
                </Button>
                <p className="text-xs text-muted-foreground mt-2">
                  {language === 'de' ? 'Unsere Spezialisten klären Details, Kompatibilität und Lieferoptionen.' :
                   language === 'es' ? 'Nuestros especialistas aclararán detalles, compatibilidad y opciones de suministro.' :
                   'Our specialists will clarify details, compatibility, and supply options.'}
                </p>
              </div>
            </div>
          </div>

          {/* Description Section */}
          {productDescription && (
            <section className="mb-8">
              <Card className="bg-card border-border">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Info className="w-5 h-5 text-primary" />
                    <h2 className="text-xl font-bold text-foreground">{t('product.description')}</h2>
                  </div>
                  <p className="text-muted-foreground leading-relaxed mb-6">{productDescription}</p>
                  
                  <Separator className="my-6" />
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="font-semibold text-foreground mb-3">{t('product.specifications')}</h3>
                      <ul className="space-y-2 text-sm text-muted-foreground">
                        <li className="flex justify-between py-1 border-b border-border/50">
                          <span>{t('product.category')}:</span>
                          {product.type ? (
                            <Link
                              to={getLocalizedPath(`/products?type=${encodeURIComponent(product.type)}`)}
                              className="font-medium text-primary hover:underline"
                            >
                              {product.type}
                            </Link>
                          ) : (
                            <span className="font-medium text-foreground">{t('product.comingSoon')}</span>
                          )}
                        </li>
                        <li className="flex justify-between py-1 border-b border-border/50">
                          <span>{t('product.manufacturer')}:</span>
                          {product.manufacturer ? (
                            <Link
                              to={getLocalizedPath(`/products?manufacturer=${encodeURIComponent(product.manufacturer)}`)}
                              className="font-medium text-primary hover:underline"
                            >
                              {product.manufacturer}
                            </Link>
                          ) : (
                            <span className="font-medium text-foreground">{t('product.comingSoon')}</span>
                          )}
                        </li>
                        <li className="flex justify-between py-1 border-b border-border/50">
                          <span>{t('product.productId')}:</span>
                          <code className="font-mono font-medium text-foreground">{product.id}</code>
                        </li>
                        <li className="flex justify-between py-1">
                          <span>{t('product.deliveryTime')}:</span>
                          <span className="font-medium text-foreground">{t('product.deliveryDays')}</span>
                        </li>
                      </ul>
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-3">{t('product.guarantees')}</h3>
                      <ul className="space-y-2 text-sm text-muted-foreground">
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                          {t('product.guarantee1')}
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                          {t('product.guarantee2')}
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                          {t('product.guarantee3')}
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                          {t('product.guarantee4')}
                        </li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </section>
          )}

          {/* Applications Block */}
          {applicationsText && (
            <section className="mb-8">
              <Card className="bg-gradient-to-br from-primary/5 to-accent/5 border-primary/20">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Wrench className="w-6 h-6 text-primary" />
                    <h3 className="text-lg font-semibold text-foreground">
                      {language === 'de' ? 'Mögliche Anwendungen' :
                       language === 'es' ? 'Aplicaciones posibles' : 'Possible Applications'}
                    </h3>
                  </div>

                  <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                    {applicationsText}
                  </p>

                  {!applicationsIsTranslated && (
                    <p className="text-xs text-muted-foreground/70 italic mb-4">
                      {language === 'de' ? 'Übersetzung folgt in Kürze — Text derzeit in einer anderen Sprache.' :
                       language === 'es' ? 'Traducción disponible próximamente — texto mostrado en otro idioma.' :
                       'Translation coming soon — text currently shown in another language.'}
                    </p>
                  )}

                  <Separator className="my-4" />

                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {language === 'de'
                      ? 'Dieser Abschnitt wird automatisch generiert, um den Katalog aktuell zu halten. Sie sind nicht sicher, ob dieses Produkt für Ihre Anwendung geeignet ist? Kontaktieren Sie uns für Klärung: '
                      : language === 'es'
                      ? 'Esta sección se genera automáticamente para mantener el catálogo actualizado. ¿No está seguro si este producto es adecuado para su aplicación? Contáctenos para aclaración: '
                      : 'This section is generated automatically to keep the catalog up to date. Not sure if this product fits your application? Contact us for clarification: '}
                    <a href="mailto:info@kmkit.com" className="text-primary hover:underline">info@kmkit.com</a>
                  </p>
                </CardContent>
              </Card>
            </section>
          )}

          {/* Related Products */}
          {relatedProducts.length > 0 && (
            <section className="mb-8">
              <h2 className="text-xl font-bold text-foreground mb-4">{t('product.related')}</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {relatedProducts.map((item, index) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                  >
                    <Link to={getLocalizedPath(`/products/${item.id}`)}>
                      <Card className="overflow-hidden hover:shadow-md transition-shadow cursor-pointer group border-border">
                        <div className="aspect-square relative bg-muted/20 max-h-[140px]">
                          <img
                            src={getSizedGoogleImage(item.image, 280) || FALLBACK_IMAGE}
                            alt={item['name_' + language] || item.name_source || t('product.comingSoon')}
                            className="w-full h-full object-contain p-2 group-hover:scale-105 transition-transform duration-300"
                            loading="lazy"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = FALLBACK_IMAGE;
                            }}
                          />
                        </div>
                        <CardContent className="p-3">
                          <h3 className="text-sm font-medium text-foreground line-clamp-2 mb-1 group-hover:text-primary transition-colors">
                            {item['name_' + language] || item.name_source || t('product.comingSoon')}
                          </h3>
                          <p className="text-xs text-muted-foreground">{t('product.onRequest')}</p>
                        </CardContent>
                      </Card>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </section>
          )}

        </motion.div>
      </main>





      <Footer />
    </div>
  );
};

export default ProductDetail;